import { useState, useEffect, useCallback } from "react";
import { Plus, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getArticleTags,
  createArticleTag,
  updateArticleTag,
  deleteArticleTag,
  TAXONOMY_SLUG_PATTERN,
  TAXONOMY_SLUG_MAX,
  CATEGORY_NAME_MAX,
  ARTICLE_TAG_NAME_MAX,
  readApiError,
} from "./supportApi";
import SupportConfirmationModal from "./SupportConfirmationModal";

/**
 * Categories and tags are structurally identical — slug, name, admin-curated — so one
 * component drives both tabs and only the endpoints and the name limit differ.
 */
const TABS = {
  categories: {
    label: "Categories",
    noun: "category",
    plural: "categories",
    nameMax: CATEGORY_NAME_MAX,
    list: getCategories,
    read: (data) => data.categories || [],
    create: createCategory,
    update: updateCategory,
    remove: deleteCategory,
    // An article has one category, so removing it leaves the article without one.
    deleteNote: (n) =>
      n
        ? `${n} ${n === 1 ? "article is" : "articles are"} in this category. They will stay, but lose it.`
        : "No articles are in this category.",
  },
  tags: {
    label: "Tags",
    noun: "tag",
    plural: "tags",
    nameMax: ARTICLE_TAG_NAME_MAX,
    list: getArticleTags,
    read: (data) => data.tags || [],
    create: createArticleTag,
    update: updateArticleTag,
    remove: deleteArticleTag,
    deleteNote: (n) =>
      n
        ? `${n} ${n === 1 ? "article carries" : "articles carry"} this tag. It will be removed from them.`
        : "No articles carry this tag.",
  },
};

/** "Withdrawal Fees" -> "withdrawal-fees". A starting point, still editable. */
const slugify = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TAXONOMY_SLUG_MAX);

export default function ArticleTaxonomy() {
  const [activeTab, setActiveTab] = useState("categories");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Set once the user edits the slug directly, so we stop overwriting their choice.
  const [slugTouched, setSlugTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [deletingId, setDeletingId] = useState(null);

  const tab = TABS[activeTab];

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await TABS[activeTab].list();
      setItems(TABS[activeTab].read(data));
    } catch (error) {
      console.error("Error fetching taxonomy:", error);
      toast.error(`Failed to load ${TABS[activeTab].plural}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setSlug("");
    setSlugTouched(false);
    setShowForm(false);
  };

  // Switching tabs must not carry a half-filled form or the other tab's rows across.
  const switchTab = (next) => {
    if (next === activeTab) return;
    resetForm();
    setItems([]);
    setActiveTab(next);
  };

  const startEdit = (item) => {
    setEditing(item);
    setName(item.name);
    setSlug(item.slug);
    setSlugTouched(true);
    setShowForm(true);
  };

  const handleNameChange = (value) => {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const slugIsValid = TAXONOMY_SLUG_PATTERN.test(slug);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    // Checked here so the author sees it next to the field rather than as a failed save.
    if (!slugIsValid) {
      toast.error(
        "Slug may contain lowercase letters, numbers and single hyphens only",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { name: name.trim(), slug };
      const response = editing
        ? await tab.update(editing.id, payload)
        : await tab.create(payload);

      if (response.ok) {
        toast.success(editing ? `${tab.label} updated` : `${tab.label} created`);
        resetForm();
        await fetchItems();
        return;
      }
      // A duplicate slug comes back as "The slug "x" is already in use".
      toast.error(await readApiError(response, `Could not save the ${tab.noun}`));
    } catch (error) {
      console.error("Error saving taxonomy item:", error);
      toast.error(`Could not save the ${tab.noun}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const item = deleteModal.item;
    if (!item) return;

    setDeletingId(item.id);
    try {
      const response = await tab.remove(item.id);
      if (response.ok) {
        toast.success(`${tab.label} deleted`);
        if (editing?.id === item.id) resetForm();
        await fetchItems();
      } else {
        toast.error(await readApiError(response, `Could not delete the ${tab.noun}`));
      }
    } catch (error) {
      console.error("Error deleting taxonomy item:", error);
      toast.error(`Could not delete the ${tab.noun}. Please try again.`);
    } finally {
      setDeletingId(null);
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  const inputClass =
    "rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Article <span className="text-gradient-ginox">Categories &amp; Tags</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          How articles are grouped and labelled. An article sits in one category and can
          carry several tags.
        </p>
      </div>

      {/* Tabs */}
      <div className="md:flex md:flex-row flex flex-col gap-3 mb-8">
        {Object.entries(TABS).map(([key, config]) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            className={
              activeTab === key
                ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green"
                : "rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            }
          >
            {config.label}
          </button>
        ))}
      </div>

      <div className="max-w-4xl space-y-5">
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add {tab.noun}
          </button>
        )}

        {/* Create / edit form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-border/70 bg-card p-5 space-y-4"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {editing ? `Edit ${tab.noun}` : `New ${tab.noun}`}
            </p>

            <div className="md:flex md:gap-4 flex flex-col gap-4">
              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  maxLength={tab.nameMax}
                  placeholder="Withdrawals"
                  className={inputClass}
                />
              </div>
              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value);
                  }}
                  maxLength={TAXONOMY_SLUG_MAX}
                  placeholder="withdrawals"
                  className={`${inputClass} font-mono ${
                    slug && !slugIsValid ? "border-red-500/50" : ""
                  }`}
                />
                <span className="mt-1 text-[10px] text-muted-foreground">
                  {slug && !slugIsValid
                    ? "Lowercase letters, numbers and single hyphens only."
                    : "Appears in help center links."}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isSubmitting && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                {isSubmitting ? "Saving..." : editing ? "Save changes" : "Create"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={isSubmitting}
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* List */}
        {isLoading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertCircle
              className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-yellow-400 text-sm">
              No {tab.plural} yet. Add one above, then apply it from the article editor.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
              <thead>
                <tr>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Name
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Slug
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Articles
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-white/[0.04] transition">
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                      {item.name}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm font-mono text-muted-foreground">
                      {item.slug}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm tabular-nums text-muted-foreground">
                      {item.articleCount ?? 0}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex flex-col justify-center items-center gap-2 text-center">
                        <button
                          onClick={() => startEdit(item)}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, item })}
                          disabled={deletingId === item.id}
                          className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                            deletingId === item.id
                              ? "border border-border/40 bg-white/[0.03] text-muted-foreground cursor-not-allowed"
                              : "border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                          }`}
                        >
                          {deletingId === item.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SupportConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={handleDelete}
        title={`Delete ${tab.noun}`}
        // The count matters: deleting is silent on the article side otherwise.
        message={`Delete "${deleteModal.item?.name}"? ${tab.deleteNote(
          deleteModal.item?.articleCount ?? 0,
        )}`}
        loading={deletingId !== null}
      />
    </div>
  );
}
